import { Sun, CloudSun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog, MapPin } from "@phosphor-icons/react";
import useWeather from "@/hooks/useWeather";

/**
 * Text-only weather stack for the Layout D "airy split" masthead.
 *
 * Renders as a two-line stack aligned to the right of the top row:
 *   line 1: [icon] 64°F · 18°C
 *   line 2: ↑ 75° · ↓ 63° · 💧 36%   (mono, uppercase, muted)
 *
 * No chip-borders, no coloured boxes — deliberately quieter than the
 * standalone WeatherWidget so the top of the panel stops competing
 * with the greeting. Consumes the shared `useWeather` hook, so this
 * surface doesn't trigger an extra geolocation prompt or fetch.
 */

const WMO = {
  0: { label: "Clear", Icon: Sun },
  1: { label: "Mainly clear", Icon: Sun },
  2: { label: "Partly cloudy", Icon: CloudSun },
  3: { label: "Overcast", Icon: Cloud },
  45: { label: "Fog", Icon: CloudFog },
  48: { label: "Rime fog", Icon: CloudFog },
  51: { label: "Light drizzle", Icon: CloudRain },
  53: { label: "Drizzle", Icon: CloudRain },
  55: { label: "Heavy drizzle", Icon: CloudRain },
  61: { label: "Light rain", Icon: CloudRain },
  63: { label: "Rain", Icon: CloudRain },
  65: { label: "Heavy rain", Icon: CloudRain },
  71: { label: "Light snow", Icon: CloudSnow },
  73: { label: "Snow", Icon: CloudSnow },
  75: { label: "Heavy snow", Icon: CloudSnow },
  77: { label: "Snow grains", Icon: CloudSnow },
  80: { label: "Rain showers", Icon: CloudRain },
  81: { label: "Rain showers", Icon: CloudRain },
  82: { label: "Violent showers", Icon: CloudRain },
  85: { label: "Snow showers", Icon: CloudSnow },
  86: { label: "Snow showers", Icon: CloudSnow },
  95: { label: "Thunderstorm", Icon: CloudLightning },
  96: { label: "Thunderstorm · hail", Icon: CloudLightning },
  99: { label: "Thunderstorm · hail", Icon: CloudLightning },
};

/** Small mono chip for the "Enable weather" fallback. Same visual
 *  weight as the dateline so the row still reads as a two-line stack. */
function EnableChip({ onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid="weather-enable"
      className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"
      title="Enable weather"
    >
      <MapPin size={11} /> Enable weather
    </button>
  );
}

export default function MastheadWeather() {
  const state = useWeather();

  if (state.status === "loading" || state.status === "idle") {
    // Reserve the row height so the layout doesn't jump when it resolves.
    return (
      <div className="text-right leading-tight" data-testid="masthead-weather-loading">
        <div className="text-sm text-muted-foreground">Locating…</div>
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground/70 mt-0.5">&nbsp;</div>
      </div>
    );
  }

  if (state.status === "denied" || state.status === "unsupported" || state.status === "error") {
    return (
      <div className="text-right" data-testid="masthead-weather-fallback">
        <EnableChip onClick={state.refetch} />
      </div>
    );
  }

  const meta = (state.code != null && WMO[state.code]) || { label: "—", Icon: Sun };
  const Icon = meta.Icon;
  const peekParts = [];
  if (state.hiF != null) peekParts.push(`↑ ${state.hiF}°`);
  if (state.loF != null) peekParts.push(`↓ ${state.loF}°`);
  if (state.rain != null) peekParts.push(`💧 ${state.rain}%`);
  return (
    <div className="text-right leading-tight" data-testid="masthead-weather">
      <div className="inline-flex items-center gap-1.5 justify-end text-sm text-foreground">
        <Icon size={14} weight="fill" className="text-brass" />
        <span className="font-medium">{state.tempF}°F</span>
        {state.tempC != null && (
          <span className="text-muted-foreground text-xs">· {state.tempC}°C</span>
        )}
      </div>
      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
        {peekParts.length ? peekParts.join(" · ") : meta.label}
      </div>
    </div>
  );
}

/**
 * Compact left-side location + condition tag for the dateline stack.
 * Deliberately no icon — it's the calm mate to the mono dateline above
 * it, and the sun/moon iconography now lives on the sunrise-sunset
 * rail below the greeting.
 */
export function MastheadLocation() {
  const state = useWeather();
  if (state.status !== "ok") return null;
  const meta = (state.code != null && WMO[state.code]) || { label: "—" };
  const parts = [];
  if (state.place) parts.push(state.place);
  if (meta.label && meta.label !== "—") parts.push(meta.label);
  if (!parts.length) return null;
  return (
    <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground" data-testid="masthead-location">
      {parts.join(" · ")}
    </div>
  );
}
